import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {BrowserDetectUtil} from 'sharedGuiSmall/utils/browser-detect-util';
import { DomSmallUtils } from 'sharedGuiSmall/utils/dom-utils';
import {  Utils } from 'sharedGuiSmall/utils/utils';
import { BoundListComponent } from './boundlist.component';
import { ItemData } from './item-data';
import { TranslateService } from '@ngx-translate/core';

const CUSTOM_COMBO_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ComboComponent),
    multi: true
};

@Component({
    selector: 'combo',
    templateUrl: './combo.component.html',
    styleUrls: ['./combo.component.less'],
    providers: [CUSTOM_COMBO_CONTROL_VALUE_ACCESSOR]
})
export class ComboComponent implements ControlValueAccessor {


    noResultsFoundText = $localize`No results found`;

    @Input()
    useFullWidth = false;

    @Input()
    public hint: string = 'hint';

    @Input()
    public label: string = '';

    @Input()
    height: number = 20;

    @Input()
    listMaxHeight: number = 180;

    @Input()
    width: number = 100;
    @Input()
    backgroundColor: string = '';

    @Input()
    showImg: boolean = true;

    @Input()
    imgFromItems: boolean = false;

    @Input()
    isNewCompany: boolean = false;

    @Input()
    isUpperMenuCombo: boolean = false;

    isImgShowing: boolean = false;

    @Input()
    inputMargin: number = 0;

    @Input()
    hasOutline: boolean = true;

    @Input()
    inputTextClass: string = "defaultTxtCls";

    @Input()
    selectedItemClass: string = '';

    @Input()
    protected showCustomDatesBtn: boolean = false;

    @ViewChild('main')
    mainElement!: ElementRef<HTMLDivElement>;

    @ViewChild('topLabel')
    topLabel!: ElementRef<HTMLDivElement>;

    @ViewChild('topSpace')
    topSpace!: ElementRef<HTMLDivElement>;

    @ViewChild('inputArea')
    inputAreaElement!: ElementRef<HTMLDivElement>;

    @ViewChild('input')
    inputElement!: ElementRef<HTMLInputElement>;

    @ViewChild('firstTd')
    firstTd!: ElementRef<HTMLTableCellElement>;

    private firstTdWidth: number = 0;

    @ViewChild('trigger')
    trigger!: ElementRef;

    @ViewChild('inputContainer')
    inputContainer!: ElementRef<HTMLTableRowElement>;

    @ViewChild("value")
    valueSpan!: ElementRef<HTMLSpanElement>;

    @Input()
    triggerWidth: number = 20;

    @Input()
    testId: string = "";

    @Input()
    public ComboData: Array<ItemData> = [];

    @Input() isTextCentered: boolean = false;

    private _value: string = '';
    private _prevValue: string = '';

    displayValue: string = '';
    displayIconCls: string = '';

    @Input()
    public autocomplete: boolean = false;

    @Input()
    disabled: boolean = false;

    @Input()
    public disabledItemClass: string = '';

    @Input()
    public emptyDisplayValue: string = '';

    @Input()
    public usePrevValue: boolean = true;

    @Input()
    public boundListWidth: number = 0;

    @Input()
    public zIndex: number = 0;

    @Input()
    public currencyCombo: boolean = false;

    @Input()
    public isReadonly: boolean | undefined = false;

    @Output() selectitem: EventEmitter<any> = new EventEmitter();

    @Output() changeCaller: EventEmitter<any> = new EventEmitter();

    @Output()
    private customDatesClick: EventEmitter<any> = new EventEmitter();

    @Output() public newCompanyClick: EventEmitter<any> = new EventEmitter();

    listRef!: ComponentRef<BoundListComponent>;
    boundlist: BoundListComponent | undefined;

    app: any;

    inputWidth: number = 0;

    exceededValueWidth = false;

    additionalListStyles: any[] = [];

    @Input()
    labelClass: string = "lab";

    initialLeftPad: number = 8;
    initialRightPad: number = 1;

    get getWidth() {
        return this.useFullWidth ? '100%' : this.width + 'px';
    }

    get getInputWidth() {
        return this.useFullWidth && !this.exceededValueWidth ? '100%' : this.imgFromItems ? this.inputWidth - 22 : this.inputWidth;
    }

    get valueWidth() {
        return this.valueSpan?.nativeElement?.offsetWidth ?? 0;
    }

    private scrollHorizontalEl: HTMLElement | undefined;
    private scrollVerticalEl: HTMLElement | undefined;

    private initialScrollLeft: number = 0;
    private initialScrollTop: number = 0;

    private scrollVerticalListener: Function | undefined;
    private scrollHorizontalListener: Function | undefined;

    @HostListener('document:click', ['$event'])
    @HostListener('document:touchstart', ['$event'])
    handleClickEvent($event: Event) {
        if (this.boundlist) {
            if (!this.getElement()?.contains($event.target as Node)
                && !this.boundlist?.getElement()?.contains($event.target as Node))
            {
                this.hideBoundList();
            }
        }
    }

    constructor(private element: ElementRef,
        private renderer: Renderer2,
        private chDet: ChangeDetectorRef,
        private viewContainerRef: ViewContainerRef,
        private translate: TranslateService) {

        this.handleCustomDatesClick = this.handleCustomDatesClick.bind(this);
    }

    ngOnInit(): void {
        this.value = this._value;
        this.translate.onLangChange.subscribe(() => {
            this.resetValue();
        })
    }

    ngAfterViewInit(): void {

        this.handleChange();
        
        const triggerWidth = this.triggerWidth;
        if (this.trigger) this.renderer.setStyle(this.trigger.nativeElement, "width", triggerWidth + 'px');

        if (this.inputMargin && this.inputElement) this.renderer.setStyle(this.inputElement.nativeElement, "marginLeft", this.inputMargin + 'px');

        const topLabelHeight = DomSmallUtils.height(this.topLabel?.nativeElement);
        const topSpaceHeight = DomSmallUtils.height(this.topSpace?.nativeElement);

        this.renderer.setStyle(this.inputAreaElement.nativeElement, "height", (this.height - topLabelHeight - topSpaceHeight) + 'px');
        this.renderer.setStyle(this.inputAreaElement.nativeElement, "width", this.getWidth);
        if (this.inputElement) this.renderer.setStyle(this.inputElement.nativeElement, "height", (this.height - topLabelHeight - topSpaceHeight - (this.hasOutline ? 3 : 0)) + 'px');
        this.renderer.setStyle(this.mainElement.nativeElement, "height", this.height + 'px');
        this.renderer.setStyle(this.mainElement.nativeElement, "width", this.getWidth);
        this.renderer.setStyle(this.element.nativeElement, "width", this.getWidth);
        this.renderer.setStyle(this.element.nativeElement, "height", this.height + 'px');

        if (!this.autocomplete && this.inputElement) this.renderer.setAttribute(this.inputElement.nativeElement, "readonly", "true");
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes && changes['ComboData'])
            this.setValue(this._value);
    }

    ngOnDestroy(): void {
        if (this.boundlist) this.viewContainerRef.remove(this.viewContainerRef.indexOf(this.listRef.hostView));
    }

    getActualHeight(): number {

        const topLabelHeight = DomSmallUtils.height(this.topLabel?.nativeElement);
        const topSpaceHeight = DomSmallUtils.height(this.topSpace?.nativeElement);
        const inputAreaHeight = DomSmallUtils.height(this.inputAreaElement?.nativeElement);

        return topLabelHeight + topSpaceHeight + inputAreaHeight;
    }

    getElement(): Element {

        return this.element.nativeElement;
    }

    handleChange(): void {
        if (this.valueSpan && this.displayValue)
        {
            this.renderer.setProperty(this.valueSpan.nativeElement, "innerHTML", this.displayValue);
        }
        this.isImgShowing = Utils.isValue(this._value) ? false : this.showImg;
        this.chDet.detectChanges();
        if (this.firstTd)
            this.firstTdWidth = this.firstTd.nativeElement.offsetWidth;
        if (!this.useFullWidth)
        {
            if (this.inputElement)
            {
                const inputContainerCss = getComputedStyle(this.inputContainer.nativeElement);
                const el = this.element.nativeElement as HTMLElement;

                const rightPadValue = parseInt(inputContainerCss.paddingRight);
                const rightPad = isNaN(rightPadValue) || rightPadValue == 0 ? this.initialRightPad : rightPadValue;

                const isVisible = el.offsetHeight != 0 && el.offsetWidth != 0 && getComputedStyle(el).display != 'none';
                let leftPad = 0;

                if (isVisible && this.firstTd?.nativeElement) {
                    leftPad = this.firstTd?.nativeElement.offsetLeft;
                }
                else {
                    const leftPadValue = parseInt(inputContainerCss.paddingLeft);
                    leftPad = isNaN(leftPadValue) || leftPadValue == 0 ? this.initialLeftPad : leftPadValue;
                }

                const paddedOffset = rightPad + leftPad;
                const mOffset = (this.inputElement?.nativeElement?.parentElement?.offsetLeft ?? 0) + paddedOffset + rightPad;
                this.inputWidth = this.width - (this.isImgShowing ? this.firstTdWidth : 0) - mOffset - this.triggerWidth;
            }
            if (this.imgFromItems)
                this.inputWidth = this.inputWidth - 22;
        }
        else if (this.displayValue && this.inputElement && this.inputElement.nativeElement.clientWidth < this.valueWidth)
        {
            this.inputWidth = 0;
            this.exceededValueWidth = true;
        }
    }

    setBoundList(list: BoundListComponent): void {

        this.boundlist = list;
    }

    createBoundList(data: Array<ItemData>): BoundListComponent {

        // Create component in its own ViewContainerRef
        //let ref = this.appViewRef.createComponent(factory, null, this.appViewRef.injector);
        let ref = this.viewContainerRef.createComponent(BoundListComponent);

        // Move BoundList from combo DOM to body
        this.renderer.appendChild(document.body, ref.location.nativeElement);

        this.listRef = ref;

        let list = <BoundListComponent>ref.instance;
        list.setCombo(this);
        list.setData(data);
        list.setTestId(this.testId);
        list.setMaxHeight(this.listMaxHeight);
        list.setTextCentered(this.isTextCentered);
        list.showCustomDatesBtn = this.showCustomDatesBtn;
        list.customDatesClick = this.handleCustomDatesClick;
        list.currencyCombo = this.currencyCombo;
        if (this.boundListWidth) list.setWidth(this.boundListWidth);
        if (this.isNewCompany) list.addNewCompanyBtn();
        if (this.isUpperMenuCombo) list.isUpperMenuCombo = true;
        this.additionalListStyles.forEach((style) => { list.setBoundlistStyle(style) });

        return list;
    }

    removeBoundList(): void {
        if (!this.boundlist) return;
        this.viewContainerRef.remove(this.viewContainerRef.indexOf(this.listRef.hostView));
        this.boundlist = undefined;
    }

    hideBoundList(): void {
        this.removeBoundList();
        this.resetInitialScrolls();
    }

    handleCustomDatesClick(): void {
        if (this.customDatesClick)
            this.customDatesClick.emit();
    }

    setInitialScrolls() {

        const parentsScroll = DomSmallUtils.parentsByTag(this.element.nativeElement, 'scrolldiv');

        if (parentsScroll.length) {

            this.scrollHorizontalEl = parentsScroll[0];
            this.scrollVerticalEl = parentsScroll[0];
        }

        if (!this.scrollVerticalEl) return;

        if (this.scrollHorizontalEl && this.scrollVerticalEl && !Utils.isScrolledHorizontally(this.scrollHorizontalEl) && !Utils.isScrolledVertically(this.scrollVerticalEl)) {

            const scroll = DomSmallUtils.parentsByTag(this.scrollHorizontalEl, 'scrolldiv');

            if (scroll.length) {

                this.scrollHorizontalEl = scroll[0];
                this.scrollVerticalEl = scroll[0];
            }
        }

        if (!this.scrollVerticalEl) return;

        if (this.scrollHorizontalEl && !Utils.isScrolledHorizontally(this.scrollHorizontalEl)) {

            const scroll = DomSmallUtils.parentsByTag(this.scrollHorizontalEl, 'scrolldiv');

            if (scroll.length && Utils.isScrolledHorizontally(scroll[0]))
                this.scrollHorizontalEl = scroll[0];
            else
                this.scrollHorizontalEl = undefined;
        }

        if (this.scrollVerticalEl && !Utils.isScrolledVertically(this.scrollVerticalEl)) {

            const scroll = DomSmallUtils.parentsByTag(this.scrollVerticalEl, 'scrolldiv');
            if (scroll.length && Utils.isScrolledVertically(scroll[0]))
                this.scrollVerticalEl = scroll[0];
            else
                this.scrollVerticalEl = undefined;
        }

        if (!this.scrollVerticalEl && !this.scrollHorizontalEl) return;

        if (this.scrollVerticalEl && this.scrollVerticalEl === this.scrollHorizontalEl) {

            this.initialScrollLeft = this.scrollVerticalEl.scrollLeft;
            this.initialScrollTop = this.scrollVerticalEl.scrollTop;

            this.scrollVerticalListener = this.renderer.listen(this.scrollVerticalEl, 'scroll', () => {
                this.onScrollVertical.bind(this)();
                this.onScrollHorizontal.bind(this)();
            });
        }
        else {

            if (this.scrollVerticalEl) {

                this.initialScrollTop = this.scrollVerticalEl.scrollTop;

                this.scrollVerticalListener = this.renderer.listen(this.scrollVerticalEl, 'scroll', this.onScrollVertical.bind(this));
            }

            if (this.scrollHorizontalEl) {

                this.initialScrollLeft = this.scrollHorizontalEl.scrollLeft;

                this.scrollHorizontalListener = this.renderer.listen(this.scrollHorizontalEl, 'scroll', this.onScrollHorizontal.bind(this));
            }
        }
    }

    resetInitialScrolls() {

        this.initialScrollLeft = 0;
        this.initialScrollTop = 0;

        if (this.scrollVerticalListener) this.scrollVerticalListener();
        if (this.scrollHorizontalListener) this.scrollHorizontalListener();

        this.scrollHorizontalEl = undefined;
        this.scrollVerticalEl = undefined;
    }

    private onScrollVertical(): void {

        if (!this.scrollVerticalEl) return;

        const boundList = this.getBoundList(undefined);

        const offset = DomSmallUtils.offset(this.scrollVerticalEl);
        const height = DomSmallUtils.height(this.scrollVerticalEl);

        this.renderer.setStyle(boundList.getElement(), 'top', (boundList.getInitTopPosition() - (this.scrollVerticalEl.scrollTop - this.initialScrollTop)) + 'px');

        if (!isInView(this.getElement(), offset.top, offset.top + height)) this.hideBoundList();
    }

    private onScrollHorizontal(): void {

        if (!this.scrollHorizontalEl) return;

        const boundList = this.getBoundList(undefined);
        const offset = DomSmallUtils.offset(this.scrollHorizontalEl);
        const width = DomSmallUtils.width(this.scrollHorizontalEl);
        this.renderer.setStyle(boundList.getElement(), 'left', (boundList.getInitLeftPosition() - (this.scrollHorizontalEl.scrollLeft - this.initialScrollLeft)) + 'px');

        if (!isInViewHorizontal(this.getElement(), offset.left, offset.left + width)) this.hideBoundList();
    }

    onInputAreaActivate(ev?: MouseEvent): void {
        if (this.boundlist) {
            this.hideBoundList();
            return;
        }

        let boundlist = this.getBoundList(this.ComboData);
        this.setBoundListData(this.ComboData);

        if (this.zIndex > 0) {
            boundlist.show(this.zIndex);

            boundlist.getAfterViewInit().subscribe(() => {
                boundlist.setZIndex(this.zIndex);
            });
        } else
            boundlist.show();

        this.inputElement?.nativeElement?.select();
    }

    onFocusOut(event: FocusEvent): void {
        if (this.boundlist &&
            event.relatedTarget != this.boundlist.getMainEl() &&
            !(this.mainElement?.nativeElement).contains(event.relatedTarget as Node)
        ) {
            this.hideBoundList();
            this.resetValue();
        }
    }

    private getBoundList(data: Array<ItemData> | undefined): BoundListComponent {
        if (!this.boundlist) this.boundlist = this.createBoundList(data ? data : this.ComboData);
        return this.boundlist;
    }

    private setBoundListData(data: Array<ItemData>): void {
        if (data) {
            this.boundlist!.setData(data);
            if (data.length == 0) {
                let noResults = new ItemData();
                noResults.id = 0;
                noResults.value = this.noResultsFoundText;
                noResults.disabled = true;
                data.push(noResults);
            }
        }
    }

    onInput(): void {
        if (!this.autocomplete || !this.inputElement) return;

        const word = this.inputElement.nativeElement.value;
        let selectedData: Array<ItemData> = [];
        if (this.ComboData) this.ComboData.forEach((item: ItemData) => {

            if (item.value.toLowerCase().indexOf(word.toLowerCase()) !== -1) selectedData.push(item);

        });

        this.getBoundList(selectedData);
        this.setBoundListData(selectedData);
        this.boundlist!.show();
        this.setValue('');
    }

    onInputAreaBlur(event: FocusEvent): void {
        if (BrowserDetectUtil.isIe()) return;
        if (this.boundlist &&
            event.relatedTarget != this.boundlist.getMainEl() &&
            !(this.mainElement?.nativeElement).contains(event.relatedTarget as Node)) {
            this.hideBoundList();
            this.resetValue();
        }
    }

    getDisplayValue(): string {
        let v: string = this.autocomplete ? this.displayValue : '';

        if (this.ComboData) this.ComboData.forEach((item) => {
            if (item.id == this.value) {
                v = item.value;

                if (item.imgCls)
                    this.displayIconCls = item.imgCls;
            }
        });

        if (Utils.isEmpty(v) && !Utils.isEmpty(this.emptyDisplayValue))
            v = this.emptyDisplayValue;

        return v;
    }

    resetValue() {

        const oldV = this.value;

        if (this.usePrevValue)
            this.value = Utils.isValue(this._prevValue) ? this._prevValue : this.value;

        if (!Utils.isValue(this.getValue())) this.displayValue = this.emptyDisplayValue;

        if (oldV !== this.value) this.changeCaller.emit(this.value);
    }

    setValue(val: string|any) {

        let oldV = this.value;

        if (Utils.isValue(this.value)) {
            this._prevValue = this.value;
        }
        this.value = val;

        if (oldV != this.value) this.changeCaller.emit(val);

    }

    getValue(): any {

        return this._value;
    }

    public isLastValue(): boolean {

        let isLast: boolean = false;
        this.ComboData.forEach((it, i) => {

            if (this._value == it.id && i == this.ComboData.length - 1) isLast = true;

        });

        return isLast;
    }

    get hasPrevValue() {
        return this._prevValue?.length > 0;
    }

    get value(): any { return this._value; };

    set value(v: any) {
        if (v !== this._value) {
            this._value = v;
            this.handleChange();
            this.onChange(v);
        }
        this.displayValue = this.getDisplayValue();
        if (Utils.isValue(this.displayValue) && !Utils.isEmpty(this.displayValue) && Utils.isValue(v)) this._prevValue = this.value;

    }


    writeValue(value: any) {

        this.setValue(value);
    }

    onChange = (_: Function) => { };
    onTouched = () => { };
    registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }

}
